import { useEffect, useState } from "react";
import { ExternalLink, Pencil, Save, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const formatUpdatedAt = (value) => {
  if (!value) return "Draft policy";
  return `Last published ${new Date(value).toLocaleDateString("en-GB")}`;
};

export function SuperPrivacyPolicyPanel() {
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/public/privacy-policy")
      .then(({ data }) => {
        setPolicy(data);
        setDraft(data.content);
      })
      .catch((error) => toast.error(errMsg(error)));
  }, []);

  const openEditor = () => {
    setDraft(policy?.content || "");
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/super/privacy-policy", { content: draft });
      setPolicy(data);
      setDraft(data.content);
      setEditing(false);
      toast.success("Privacy policy published");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b pb-8"
      data-testid="super-privacy-policy-panel"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
          Service settings
        </p>
        <h2 className="mt-1 font-display text-2xl font-bold">Privacy policy</h2>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="super-privacy-policy-status">
          {policy ? formatUpdatedAt(policy.updated_at) : "Loading policy…"}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild size="sm" variant="outline" data-testid="super-privacy-policy-view-link">
          <Link to="/privacy">
            <ExternalLink className="mr-2 h-4 w-4" />
            View public page
          </Link>
        </Button>
        <Button
          size="sm"
          disabled={!policy}
          onClick={openEditor}
          data-testid="super-privacy-policy-edit-button"
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit policy
        </Button>
      </div>
      <Dialog open={editing} onOpenChange={(open) => !open && setEditing(false)}>
        <DialogContent className="max-w-4xl" data-testid="super-privacy-policy-dialog">
          <DialogHeader>
            <DialogTitle>Edit privacy policy</DialogTitle>
          </DialogHeader>
          <Textarea
            className="min-h-[460px] resize-y font-mono text-sm leading-6"
            onChange={(event) => setDraft(event.target.value)}
            value={draft}
            data-testid="super-privacy-policy-editor-input"
          />
          <DialogFooter>
            <Button
              disabled={saving}
              variant="outline"
              onClick={() => setEditing(false)}
              data-testid="super-privacy-policy-cancel-button"
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              disabled={saving}
              onClick={save}
              data-testid="super-privacy-policy-save-button"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Publishing…" : "Publish policy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}